import React, { useMemo } from "react";
import { ActiveTeamMember, CurrentCompany } from "@/types";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { SettingsFormContent, SettingsHeader } from "@/components/pages/Settings";
import { Grid } from "@mantine/core";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import RoleSelector from "@/components/organisms/RoleSelector";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTeamMember } from "@/services/Settings";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";
import Badge from "@/components/atoms/Badge";
import Group from "@/components/atoms/Group";
import { useAuth } from "@/contexts/AuthContext";
import { getApiError } from "@/utils/apiErrors";

interface UserTableNewProps {
  activeTeamMembers: ActiveTeamMember[];
  currentCompany: CurrentCompany | undefined;
  isAdmin: boolean;
  onClick: () => void;
}

const UserTable: React.FC<UserTableNewProps> = (props) => {
  const { activeTeamMembers, currentCompany, isAdmin, onClick } = props;
  const { t } = useTranslation();
  const { user, userContextIds } = useAuth();

  // userContextIds is read synchronously from localStorage, so it is populated before the
  // ["user"] query resolves - and stays populated if that query fails. Deriving identity from
  // user.id alone would silently disable the self-demotion guard in that window.
  const signedInUserId = userContextIds?.userId ?? user?.id;
  const queryClient = useQueryClient();

  const { mutate: mutateDeleteUser } = useMutation({
    mutationFn: async (userId: number) => {
      await deleteTeamMember(currentCompany?.companyId as number, userId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getCompanyTeam", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:settings.userRemoved"))
      );
    },
    onError: (error) => {
      // 243: removing the last deviation manager while deviations must be approved, as in RoleSelector.
      const isLastDeviationManager = getApiError(error).errorCode === 243;
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          isLastDeviationManager
            ? t("notifications:settings.lastDeviationManager")
            : t("notifications:settings.userRemoveError")
        )
      );
    },
  });

  const columns: ColumnDef<ActiveTeamMember>[] = useMemo(
    () => [
      {
        header: t("common:email"),
        accessorKey: "email",
      },
      {
        header: t("common:firstname"),
        accessorKey: "firstName",
      },
      {
        header: t("common:lastname"),
        accessorKey: "lastName",
      },
      {
        header: t("common:role"),
        accessorKey: "roles",
        cell: ({ row }) => {
          // Normalise once: the API has already renamed this field once, and a missing
          // array would otherwise throw here on render and in RoleSelector's change diff.
          const roles = row.original.roles ?? [];

          if (isAdmin) {
            return (
              <RoleSelector
                userId={row.original.id}
                currentRoles={roles}
                currentCompany={currentCompany}
                isSelf={row.original.id === signedInUserId}
              />
            );
          }

          return (
            <Group gap="4px">
              {roles.map((role) => (
                <Badge key={role} color="gray" data-testid={`role-badge-${row.original.id}-${role}`}>
                  {t(`enumerables:userRoles.${role}`)}
                </Badge>
              ))}
            </Group>
          );
        },
      },
    ],
    [t, currentCompany, isAdmin, signedInUserId]
  );

  const editActions = (member: ActiveTeamMember) => [
    {
      label: t("settings:delete"),
      action: () => mutateDeleteUser(member.id),
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader showDivider={false}>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:activeUsers")}
            </Text>
            {isAdmin && (
              <CTA id="settings-team-add" intent="secondary" onClick={onClick}>
                {t("settings:newUser")}
              </CTA>
            )}
          </SettingsHeader>
          <Grid.Col span={12}>
            <SettingsTable data={activeTeamMembers} columns={columns} editActions={editActions} isAdmin={isAdmin} />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
    </React.Fragment>
  );
};

export default UserTable;
